import { useState, useRef, useEffect } from "react";
import { User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useInsureds } from "@/lib/api";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

interface InsuredPickerProps {
  /** Currently linked insured id, or null when nothing is selected. */
  selectedId: string | null;
  /** Display name of the linked insured (shown once one is picked). */
  insuredName: string;
  /** Optional agency scope — mirrors the page's agency context. */
  agencyId?: string;
  /** Fired with the chosen existing insured. */
  onSelect: (insured: { id: string; name: string }) => void;
  /** Fired when the user edits the field, dropping the current selection. */
  onClear: () => void;
  /** Error styling toggle (red border) driven by the parent's validation. */
  invalid?: boolean;
}

// Picker for linking a policy to an EXISTING insured. Server-side search
// (debounced 250ms) via `useInsureds({ q })`, so the dropdown stays cheap
// regardless of how many insureds exist. Orphan ("unmatched") rows have a
// null id and are filtered out — only real, linkable insureds are offered.
// Mirrors CertificateHolderPicker's structure.
export function InsuredPicker({
  selectedId,
  insuredName,
  agencyId,
  onSelect,
  onClear,
  invalid,
}: InsuredPickerProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery, 250);
  const { data } = useInsureds({
    q: debouncedQuery,
    pageSize: 8,
    agencyId,
    enabled: showDropdown && debouncedQuery.trim().length >= 2,
  });
  const results = (data?.data ?? []).filter(
    (r): r is typeof r & { id: string } => !!r.id,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click.
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (val: string) => {
    setSearchQuery(val);
    if (!showDropdown && val.length > 0) setShowDropdown(true);
    if (selectedId) onClear();
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        id="insured"
        placeholder="Search insured by name or DOT…"
        value={selectedId ? insuredName : searchQuery}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setShowDropdown(true)}
        aria-invalid={invalid}
        className={invalid ? "border-destructive" : ""}
        data-testid="input-insured-search"
      />
      {showDropdown && results.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 top-full mt-1 left-0 right-0 bg-popover border rounded-md shadow-lg max-h-[260px] overflow-y-auto"
        >
          <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Insureds
          </div>
          <div className="p-1 pt-0">
            {results.map((insured) => (
              <button
                key={insured.id}
                type="button"
                className="w-full text-left px-3 py-2 rounded-sm text-sm hover:bg-accent hover:text-accent-foreground flex items-start gap-2 cursor-pointer"
                onClick={() => {
                  setSearchQuery("");
                  setShowDropdown(false);
                  onSelect({ id: insured.id, name: insured.name ?? "" });
                }}
                data-testid={`insured-option-${insured.id}`}
              >
                <User className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{insured.name}</div>
                  {insured.dotNumber && (
                    <div className="truncate text-[11px] text-muted-foreground">
                      DOT {insured.dotNumber}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
